/**
 * Dashboard Query Hooks
 */

import { useQuery } from '@tanstack/react-query';
import { dashboardApi, organizationsApi, goalsApi, initiativesApi, kpisApi } from '../../services/apiService';
import { queryKeys, STALE_TIMES } from '../../providers/QueryProvider';
import { DashboardData, StrategicGoalList, InitiativeList, KPIList } from '../../types';

interface DashboardQueryResult {
  dashboard: DashboardData | undefined;
  organization: any;
  goals: StrategicGoalList[];
  goalKpis: Record<number, KPIList[]>;
  initiatives: InitiativeList[];
  initiativeKpis: Record<number, KPIList[]>;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useDashboardQuery(userId?: number, year?: number) {
  return useQuery({
    queryKey: [...queryKeys.dashboard(userId), year],
    queryFn: () => dashboardApi.getData(year),
    staleTime: STALE_TIMES.DASHBOARD,
  });
}

export function useOrganizationQuery(organizationId?: number, year?: number) {
  return useQuery({
    queryKey: [...queryKeys.organizations.detail(organizationId || 0), year],
    queryFn: async () => {
      const params = year ? { year } : undefined;
      const data = await organizationsApi.get(organizationId!, params);
      // API returns { organization: {...}, statistics: {...} } — flatten the
      // statistics onto the org object so call-sites can read
      // e.g. `organization.total_goals` alongside `organization.name`.
      const raw = data as any;
      const org = raw?.organization ?? raw;
      const statistics = raw?.statistics ?? null;
      return statistics ? { ...org, statistics, ...statistics } : org;
    },
    enabled: !!organizationId,
    staleTime: STALE_TIMES.ORGANIZATIONS,
  });
}

export function useDashboardGoalsQuery(organizationId?: number) {
  return useQuery({
    // organizationId is part of the cache key so each org gets its own
    // cached entry — required when strategy_team / executive jump between
    // /dashboard/:id pages, otherwise React Query reuses one org's goal
    // list for another.
    queryKey: [...queryKeys.goals.all, 'dashboard', { organizationId: organizationId ?? null }],
    queryFn: async () => {
      // Strategy_team / executive viewing /dashboard/:id otherwise see every
      // org's goals because goalsApi.list() returned the entire visible set.
      const params: Record<string, any> = {};
      if (organizationId) params.department_id = organizationId;
      const response = await goalsApi.list(params);
      const goalsList = response.results || [];

      // Fetch KPIs for the first 6 goals in parallel — matches GOALS_PER_PAGE
      // on DashboardScreen so the whole first page renders with KPIs populated.
      const kpisMap: Record<number, KPIList[]> = {};
      const goalsToFetch = goalsList.slice(0, 6);

      await Promise.all(
        goalsToFetch.map(async (goal) => {
          try {
            const kpis = await goalsApi.getKPIs(goal.id);
            kpisMap[goal.id] = kpis.slice(0, 4);
          } catch {
            kpisMap[goal.id] = [];
          }
        })
      );

      return { goals: goalsList, kpis: kpisMap };
    },
    staleTime: STALE_TIMES.GOALS,
  });
}

// Cap how many initiatives we fan out KPI fetches for on the dashboard.
// Matches the goals cap (5) and keeps cold-load network load bounded on orgs
// with many initiatives. Tune upward only if the dashboard UI actually renders
// more than this at once.
const DASHBOARD_INITIATIVE_KPI_LIMIT = 10;

export function useDashboardInitiativesQuery(organizationId?: number) {
  return useQuery({
    // organizationId scopes the cache so each /dashboard/:id keeps its
    // own initiatives list and there's no cross-org leakage.
    queryKey: [...queryKeys.initiatives.all, 'dashboard', { organizationId: organizationId ?? null }],
    queryFn: async () => {
      // InitiativeFilter on the backend exposes ?organization=<id>; without
      // it the dashboard initiatives table mixed every org's records when
      // strategy_team inspected a specific org.
      const params: Record<string, any> = {};
      if (organizationId) params.organization = organizationId;
      const response = await initiativesApi.list(params);
      const initiativesList = response.results || [];

      // Fetch KPIs for the first N initiatives in parallel. Previously this
      // fanned out to ALL initiatives, which meant 50+ concurrent HTTP calls
      // on strategy-team dashboards during cold load.
      const kpisMap: Record<number, KPIList[]> = {};
      const initiativesToFetch = initiativesList.slice(0, DASHBOARD_INITIATIVE_KPI_LIMIT);

      await Promise.all(
        initiativesToFetch.map(async (initiative) => {
          try {
            const kpisResponse = await kpisApi.list({ initiative: initiative.id });
            kpisMap[initiative.id] = (kpisResponse.results || []).slice(0, 4);
          } catch {
            kpisMap[initiative.id] = [];
          }
        })
      );

      return { initiatives: initiativesList, kpis: kpisMap };
    },
    staleTime: STALE_TIMES.INITIATIVES,
  });
}

export default useDashboardQuery;
